import React, { useState, useEffect, Fragment } from 'react'
import { connect } from 'react-redux'
import Modal from '../../components/modal/modal'
import { bindActionCreators } from 'redux'
import moment from 'moment'
import { IconButton } from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid';
import idx from 'idx'
import { warningSnackBar } from '../../actions/common.action'
import { getResourceAllocations, updateTimesheetTask } from '../../actions/timesheet.action'
import { getTaskdropdownData } from '../../actions/project-management.action'
import { getUserList } from '../../actions/user-management.action'
import { v4 as uuidv4 } from 'uuid'
import './scss/timesheet.scss'
import MultiSelect from "../../components/inputs/multi-select";
import TimePickerInput from '../../components/inputs/time-picker'
import { getTimesheetSectionDropdownOptions } from "../../util/section-dropdown-format";
import { TIMESHEET_TASK_DROPDOWN_PARAMS } from './task-dropdown-options';
/**
 * DESCRIPTION:
 * This component provides a modal interface for allocating project resources 
 * (users) to tasks based on approved timesheet data. It enables users to:
 * Typically launched as a dialog from the Project Budget screen 
 * to handle unallocated timesheet entries.
 * 
 * - View approved timesheet entries and associated tasks
 * - Allocate or reassign users to tasks
 * - Add child (split) allocations under a parent time entry
 * - Validate overlapping time slots and ensure allocations are within bounds
 * - Filter and edit unallocated/allocated tasks
 * - Submit valid task allocations to the backend
 *
 * FEATURES:
 * - Task selection via searchable MultiSelect dropdown
 * - Inline editing of start/end times using TimePickerInput
 * - Task hierarchy: parent and child rows with validation
 * - Conflict detection for overlapping time allocations
 * - Collapse/expand toggle for grouped allocations
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.selectedTask - Selected task object.
 * @param {string} props.projectId - Active project identifier.
 * @param {string} props.projectType - Project classification type.
 * @param {boolean} props.open - Modal open state.
 * @param {Function} props.dialogClose - Handler invoked on dialog close.
 * @returns {JSX.Element} Rendered modal dialog component.
 */

function AllocateTimesheetTask({ selectedTask, projectId, projectType, open, dialogClose, ...props }) {

    const [state, setState] = useState({
        allAllocations: [],
        data: [],
        tasks: [],
        project: {},
        users: [],
        filter: {
            value: 'unallocated_tasks',
            items: [
                { key: 'Unallocated Tasks Only', value: 'unallocated_tasks' },
                { key: 'Allocated Tasks Only', value: 'allocated_tasks' },
                { key: 'All Tasks', value: 'all_tasks' }
            ]
        }
    })

    const [openRows, setOpenRows] = useState({})

    useEffect(() => {
        init();
    }, [projectId])

    const init = async () => {
        if (!state.users.length) await getUsersList()
        await getWorkAllocations();
        const response = await props.getTaskdropdownData(projectId, TIMESHEET_TASK_DROPDOWN_PARAMS);
        const taskOptions = getTimesheetSectionDropdownOptions(response || [], []);
        setState(prev => ({ ...prev, tasks: taskOptions }));
    }

    const getWorkAllocations = async () => {
        let params = { projectId, projectType, updateTaskAllocation: true }
        const resourceAllocations = await props.getResourceAllocations(params)
        let project = idx(resourceAllocations, _ => _.length) ? resourceAllocations[0].project : {}
        const allAllocations = resourceAllocations
            .filter(allocation => allocation.status === 'APPROVED')
            .map(allocation => {
                allocation['isAllocated'] = allocation.isAllocated ? true : false;
                return allocation;
            });
        setState(prev => ({ ...prev, project, allAllocations, data: allAllocations }));
    }

    const getUsersList = async () => {
        const list = await props.getUserList();
        const users = Array.isArray(list)
            ? list.map(({ id, firstName, lastName }) => ({
                key: [firstName, lastName].filter(item => item).join(' '),
                value: id
            }))
            : [];
        setState(prev => ({ ...prev, users }));
    }

    const handleMultiChange = (id, fieldName, option) => {
        let { data } = state;
        const idx = data.findIndex(d => d.id === id);
        if (idx === -1) return;
        const updated = { ...data[idx] };
        if (option?.isStage) {
            updated.stageId = option.value || '';
            updated.taskId = '';
        } else {
            updated.taskId = option?.value || '';
            updated.stageId = '';
        }
        updated.taskName = option?.label || '';
        updated.taskType = option?.taskType || '';
        updated.editing = true;
        updated.isAllocated = (updated.taskId || updated.stageId) ? true : false;
        updated.valid = true;
        updated.touched = false;
        updated.error = '';
        updated.task_error = '';
        data[idx] = updated;
        setState(prev => ({ ...prev, data }));
    };

    const getData = () => {
        let { data, filter: { value } } = state;
        return data.filter(item => {
            if (item.editing) return true;
            if (value === 'all_tasks') return true;
            if (value === 'allocated_tasks') return (item.isAllocated || item.is_parent);
            if (value === 'unallocated_tasks') return !item.isAllocated;
            return false;
        });
    }


    const getParentRows = () => {
        return getData().filter(item => !item.parentId);
    }

    const getChildRows = (parentId) => {
        return getData().filter(item => item.parentId === parentId);
    }

    const toggleRow = (id) => {
        setOpenRows(prev => ({ ...prev, [id]: !prev[id] }));
    }

    const addTimesheetData = (inp) => {
        let { data } = state;
        if (inp.parentId) {
            let updatedData = data.filter(item => item.id !== inp.id);
            let parentId = inp.parentId;
            const hasChild = updatedData.some(item => item.parentId === parentId);
            if (!hasChild) {
                updatedData = updatedData.map(item => {
                    if (item.id === parentId) {
                        return { ...item, disable: false };
                    }
                    return item;
                });
            }
            setState(prev => ({ ...prev, data: updatedData }));
        } else {
            const parentId = inp.id;
            let updatedData = data.map(item => {
                if (item.id === parentId) {
                    return { ...item, disable: true };
                }
                return item;
            });
            let childRow = { ...inp };
            childRow.parentId = parentId;
            childRow.id = uuidv4();
            childRow.inline_item = true;
            childRow.editing = true;
            childRow.disable = false;
            childRow.is_parent = false;
            childRow.valid = true;
            childRow.touched = false;
            childRow.error = '';
            childRow.task_error = '';
            childRow.startTime = inp.startTime;
            childRow.endTime = inp.endTime;
            if (!childRow.tableData) {
                childRow.tableData = {};
            }
            childRow.tableData.isTreeExpanded = true;
            updatedData.push(childRow);
            setState(prev => ({ ...prev, data: updatedData }));
        }
    };

    const handleChange = (id, parentId = null, name, value) => {
        let { data, tasks } = state;
        let rowIndex = data.findIndex(item => item.id === id);
        if (rowIndex === -1) {
            console.log("Row not found in data for id:", id);
            return;
        }
        const updatedRow = { ...data[rowIndex] };
        if (name === 'taskId') {
            let task = tasks.find(task => task.value === value);
            updatedRow[name] = value;
            updatedRow.taskType = task?.taskType || task?.type || '';
            updatedRow.taskName = task ? task.key : '';
            updatedRow.editing = true;
            updatedRow.isAllocated = value ? true : false;
        } else {
            updatedRow[name] = moment.utc(value).format('YYYY-MM-DDTHH:mmZ').toString();
        }
        updatedRow.valid = true;
        updatedRow.touched = false;
        updatedRow.error = '';
        updatedRow.task_error = '';
        data[rowIndex] = updatedRow;
        setState(prev => ({ ...prev, data }));
    };

    const handleValidation = (currentData) => {
        let isValid = true;
        const validatedData = currentData.map(item => ({
            ...item,
            valid: true,
            touched: false,
            error: '',
            task_error: ''
        }));

        validatedData.forEach(timesheet => {
            if (timesheet.parentId && !(timesheet.taskId || timesheet.stageId)) {
                timesheet.touched = true;
                timesheet.valid = false;
                timesheet.error = 'This Field is Required';
                timesheet.task_error = 'This Field is Required';
                isValid = false;
            }
        });

        const filterData = validatedData.filter(timesheet => timesheet.taskId || timesheet.stageId);

        let timesheets = {};
        for (let timesheet of filterData) {
            if (timesheet.parentId) {
                let parent = validatedData.find(item => item.id === timesheet.parentId);
                if (!parent) {
                    timesheet.touched = true;
                    timesheet.valid = false;
                    timesheet.error = 'Parent row not found';
                    isValid = false;
                    continue;
                }
                if (!timesheets[timesheet.parentId]) {
                    timesheets[timesheet.parentId] = {
                        id: parent.id,
                        startTime: parent.startTime,
                        endTime: parent.endTime,
                        allocations: filterData.filter(item => item.parentId === parent.id),
                    };
                }
            } else {
                timesheets[timesheet.id] = {
                    allocations: [timesheet]
                };
            }
        }

        for (let key in timesheets) {
            let timeGroup = timesheets[key];
            if (timeGroup.startTime && timeGroup.endTime) {
                for (const child of timeGroup.allocations) {
                    if (
                        moment(child.startTime).isBefore(moment(timeGroup.startTime)) ||
                        moment(child.endTime).isAfter(moment(timeGroup.endTime))
                    ) {
                        child.touched = true;
                        child.valid = false;
                        child.error = 'Allocation invalid. Choose<br/>Within parent schedule';
                        isValid = false;
                    }
                }
            }
            if (timeGroup.allocations.length > 1) {
                let sortedChildren = [...timeGroup.allocations].sort((a, b) => moment(a.startTime).diff(moment(b.startTime)));
                for (let i = 0; i < sortedChildren.length; i++) {
                    for (let j = i + 1; j < sortedChildren.length; j++) {
                        if (intervalsOverlap(sortedChildren[i].startTime, sortedChildren[i].endTime, sortedChildren[j].startTime, sortedChildren[j].endTime)) {
                            const childObj = validatedData.find(item => item.id === sortedChildren[j].id);
                            if (childObj) {
                                childObj.touched = true;
                                childObj.valid = false;
                                childObj.error = 'Time allocation conflict.<br/>Please reassign new time';
                            }
                            isValid = false;
                        }
                    }
                }
            }
        }

        setState(prev => ({ ...prev, data: validatedData }));
        return { validatedData, isValid };
    };

    const intervalsOverlap = (s1, e1, s2, e2) => {
        return moment(s1).isBefore(moment(e2)) && moment(s2).isBefore(moment(e1));
    };

    const submitData = async () => {
        const { validatedData, isValid } = handleValidation(state.data);
        const childParents = new Set();
        validatedData.forEach(item => {
            if (item.parentId) {
                childParents.add(item.parentId);
            }
        });
        let req = { allocations: [] };

        let valid = isValid;
        for (let timesheet of validatedData) {
            if (!timesheet.parentId && childParents.has(timesheet.id)) continue;
            if (timesheet.valid && (timesheet.taskId || timesheet.stageId))
                req.allocations.push({
                    id: timesheet.inline_item ? '' : timesheet.id,
                    userId: timesheet.userId,
                    projectId: timesheet.projectId,
                    projectType: timesheet.projectType,
                    taskId: timesheet.taskId || '',
                    stageId: timesheet.stageId || '',
                    taskType: timesheet.taskType || '',
                    taskName: timesheet.taskName,
                    startTime: timesheet.startTime,
                    endTime: timesheet.endTime,
                    parentId: timesheet.parentId ? timesheet.parentId : '',
                    allDayWithBreaks: timesheet.allDayWithBreaks,
                });
        }
        if (!valid || !req.allocations.length) return;
        await props.updateTimesheetTask(req);
        props.submitData();
    };

    const getUserName = (id) => {
        const { users } = state
        if (!id || !users) return ''
        const user = users.find(user => user.value === id)
        return user?.key || ''
    }
    const handleAddChildRow = (parent) => {
        if (!openRows[parent.id]) {
            toggleRow(parent.id);
        }
        addTimesheetData(parent);
    };
    return (
        <Modal
            open={open}
            onClose={dialogClose}
            fullWidth
            maxWidth='md'
            modalHeading={`Confirm Allocations - ${idx(state, _ => _.project.name) || ''}`}
            modalSaveTxt={'Confirm'}
            modalCloseTxt='Cancel'
            onClick={submitData}
            savebtnClass='createBtn'
            showActionBtn={true}
            primary
        >
            <div className="purchase-order-block">
                <div className="row">
                    <Grid container>
                        <Grid item sm={12}>
                            <div className='tableContainer'>
                                {getParentRows() && getParentRows().length > 0 && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="material-list-block">
                                                <div className="table-responsive">
                                                    <table className="table-create table-budget-allocate table-material table-material-ch budget-table table table-bordered">
                                                        <thead>
                                                            <tr>
                                                                <th>User</th>
                                                                <th>Task</th>
                                                                <th className='budget-allocate-date'>Date</th>
                                                                <th>Start Time</th>
                                                                <th>Finish Time</th>
                                                                <th className='text-center'>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {getParentRows().map(parent => (
                                                                <Fragment key={parent.id}>
                                                                    {/* â€” Parent row */}
                                                                    <tr>
                                                                        <td className='budget-allocate-user'>
                                                                            {getUserName(parent.userId)}
                                                                            <IconButton sx={{ p: 0 }} aria-label="toggle" onClick={() => toggleRow(parent.id)}>
                                                                                <ExpandMoreIcon />
                                                                            </IconButton>
                                                                        </td>
                                                                        <td className='budget-allocate-task'>
                                                                            <MultiSelect
                                                                                placeholder="Select Task"
                                                                                isStage={true}
                                                                                items={state.tasks}
                                                                                value={state.tasks.find(opt => opt.value === (parent.taskId || parent.stageId)) || null}
                                                                                onChangeHandler={option =>
                                                                                    handleMultiChange(parent.id, 'taskId', option)
                                                                                }
                                                                                isSearchable
                                                                                disabled={parent.disable}
                                                                            />
                                                                            {parent.task_error && <p className="budget-error-text" dangerouslySetInnerHTML={{ __html: parent.task_error }} />}
                                                                        </td>
                                                                        <td className='budget-allocate-date'>{moment.utc(parent.startTime).format('ddd, DD-MM-YYYY')}</td>
                                                                        <td className='budget-allocate-time'>
                                                                            <TimePickerInput
                                                                                format='LT'
                                                                                value={parent.startTime}
                                                                                touched={parent.touched}
                                                                                valid={parent.valid}
                                                                                disabled={parent.disable}
                                                                                onChange={value => handleChange(parent.id, null, 'startTime', value)}
                                                                            />
                                                                            {parent.error && <p className="budget-error-text" dangerouslySetInnerHTML={{ __html: parent.error }} />}
                                                                        </td>
                                                                        <td className='budget-allocate-time'>
                                                                            <TimePickerInput
                                                                                format='LT'
                                                                                value={parent.endTime}
                                                                                touched={parent.touched}
                                                                                valid={parent.valid}
                                                                                disabled={parent.disable}
                                                                                onChange={value => handleChange(parent.id, null, 'endTime', value)}
                                                                            />
                                                                            {parent.error && <p className="budget-error-text" dangerouslySetInnerHTML={{ __html: parent.error }} />}
                                                                        </td>
                                                                        <td className='text-center'>
                                                                            <IconButton sx={{ p: 0 }} aria-label="add" onClick={() => handleAddChildRow(parent)}>
                                                                                <AddBoxIcon />
                                                                            </IconButton>
                                                                        </td>
                                                                    </tr>
                                                                    {openRows[parent.id] && getChildRows(parent.id).map(child => (
                                                                        <tr key={child.id} className="budget-child-row">
                                                                            <td className='budget-allocate-user ps-4'>{getUserName(child.userId)}</td>
                                                                            <td className='budget-allocate-task'>
                                                                                <MultiSelect
                                                                                    placeholder="Select Task"
                                                                                    isStage={true}
                                                                                    items={state.tasks}
                                                                                    value={state.tasks.find(opt => opt.value === (child.taskId || child.stageId)) || null}
                                                                                    onChangeHandler={option =>
                                                                                        handleMultiChange(child.id, 'taskId', option)
                                                                                    }
                                                                                    isSearchable
                                                                                    disabled={child.disable}
                                                                                />
                                                                                {child.task_error && <p className="budget-error-text" dangerouslySetInnerHTML={{ __html: child.task_error }} />}
                                                                            </td>
                                                                            <td className='budget-allocate-date'>{moment.utc(child.startTime).format('ddd, DD-MM-YYYY')}</td>
                                                                            <td className='budget-allocate-time'>
                                                                                <TimePickerInput
                                                                                    format='LT'
                                                                                    value={child.startTime}
                                                                                    touched={child.touched}
                                                                                    valid={child.valid}
                                                                                    disabled={child.disable}
                                                                                    onChange={value => handleChange(child.id, parent.id, 'startTime', value)}
                                                                                />
                                                                                {child.error && <p className="budget-error-text" dangerouslySetInnerHTML={{ __html: child.error }} />}
                                                                            </td>
                                                                            <td className='budget-allocate-time'>
                                                                                <TimePickerInput
                                                                                    format='LT'
                                                                                    value={child.endTime}
                                                                                    touched={child.touched}
                                                                                    valid={child.valid}
                                                                                    disabled={child.disable}
                                                                                    onChange={value => handleChange(child.id, parent.id, 'endTime', value)}
                                                                                />
                                                                                {child.error && <p className="budget-error-text" dangerouslySetInnerHTML={{ __html: child.error }} />}
                                                                            </td>
                                                                            <td className='text-center delete-allocate'>
                                                                                <DeleteOutlineOutlinedIcon className='delete-icon' onClick={() => addTimesheetData(child)} />
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </Fragment>
                                                            ))}
                                                        </tbody>
                                                    </table>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Modal >
    )

}

const mapStateToProps = state => ({})

const mapDispatchToProps = dispatch => ({
    warningSnackBar: bindActionCreators(warningSnackBar, dispatch),
    getUserList: bindActionCreators(getUserList, dispatch),
    getTaskdropdownData: bindActionCreators(getTaskdropdownData, dispatch),
    getResourceAllocations: bindActionCreators(getResourceAllocations, dispatch),
    updateTimesheetTask: bindActionCreators(updateTimesheetTask, dispatch),
})

export default connect(mapStateToProps, mapDispatchToProps)(AllocateTimesheetTask)